/**
 * ProfileSettings — Settings → Profile page (PR-P8-2, Option D).
 *
 * Inline editing of all onboarding profile fields, organized in sections:
 *   - Organization (name, tier, customer types, AI system types)
 *   - Data & Scope (V112-3-10, ADR-0099: the workspace defaults every system inherits; previewed before saving)
 *   - Compliance Goals (framework selection)
 *
 * Always one click from the nav sidebar under Settings. Changes are
 * saved per-section via the existing updateProfile API.
 */

import { useEffect, useState } from 'react';
import type {
  CustomerBusinessType,
  Framework,
  ParsedCustomerProfile,
  ServiceClassificationType,
} from '@aigovops/core';
import {
  Alert,
  Button,
  CheckboxGroup,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  type CheckboxGroupOption,
} from '@aigovops/design-system';
import { getWorkspaceProfile, listFrameworks, updateProfile } from '../lib/api';
import { useWorkspace } from '../lib/workspace';
import { WorkspaceScope } from '../components/scope/WorkspaceScope';

const CLASSIFICATION_OPTIONS: CheckboxGroupOption<ServiceClassificationType>[] = [
  { value: 'agentic_systems', label: 'Agentic Systems' },
  { value: 'ai_agents', label: 'AI Agents' },
  { value: 'llm_services', label: 'LLM Services' },
  { value: 'ml_models', label: 'ML Models' },
  { value: 'data_pipelines', label: 'Data Pipelines' },
];

const CUSTOMER_TYPE_OPTIONS: CheckboxGroupOption<CustomerBusinessType>[] = [
  { value: 'b2b', label: 'B2B' },
  { value: 'b2c', label: 'B2C' },
  { value: 'b2g', label: 'B2G' },
];

interface SectionState {
  saving: boolean;
  saved: boolean;
  error: string | null;
}

export function ProfileSettings() {
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug;
  // V112-3-10 (ADR-0099 §4): saving the defaults is owner / admin / editor; keeping a system on its own answer is a
  // classification save (owner / admin).
  const canEdit = role === 'owner' || role === 'admin' || role === 'editor';
  const canKeep = role === 'owner' || role === 'admin';

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ParsedCustomerProfile | null>(null);
  const [orgName, setOrgName] = useState('');
  const [tier, setTier] = useState<string>('tier_2');
  const [customerTypes, setCustomerTypes] = useState<CustomerBusinessType[]>([]);
  const [classifications, setClassifications] = useState<ServiceClassificationType[]>([]);
  const [compliancePosture, setCompliancePosture] = useState<string[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [reload, setReload] = useState(0);
  const [frameworks, setFrameworks] = useState<Framework[]>([]);

  const [orgState, setOrgState] = useState<SectionState>({ saving: false, saved: false, error: null });
  const [goalsState, setGoalsState] = useState<SectionState>({ saving: false, saved: false, error: null });

  useEffect(() => {
    let cancelled = false;
    Promise.all([getWorkspaceProfile(slug), listFrameworks()])
      .then(([profileRes, fws]) => {
        if (cancelled) return;
        const p = profileRes.profile;
        setProfile(p);
        if (p) {
          setOrgName(p.organization_name);
          setTier(p.tier);
          setCustomerTypes(p.customer_types ?? []);
          setClassifications(p.service_classifications ?? []);
          setCompliancePosture(p.compliance_posture ?? []);
        }
        setLoadError(false);
        setFrameworks(fws);
      })
      // A failed load must not show defaults: saving them would erase the stored answers.
      .catch(() => { if (!cancelled) setLoadError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, reload]);

  async function saveSection(fields: Record<string, unknown>, setState: (s: SectionState) => void) {
    if (!profile) return;
    setState({ saving: true, saved: false, error: null });
    try {
      const res = await updateProfile(profile.profile_id, { ...fields, status: 'active' });
      if (res?.profile) setProfile(res.profile);
      setState({ saving: false, saved: true, error: null });
    } catch (err) {
      setState({ saving: false, saved: false, error: err instanceof Error ? err.message : 'Failed to save' });
    }
  }

  if (loading) return <Spinner label="Loading profile…" />;
  if (loadError) {
    return (
      <div style={{ padding: 'var(--space-6)', display: 'grid', gap: 'var(--space-3)', justifyItems: 'start' }}>
        <Alert variant="error">The profile did not load.</Alert>
        <Button variant="secondary" onClick={() => { setLoading(true); setReload((r) => r + 1); }}>Retry</Button>
      </div>
    );
  }
  if (!profile) {
    return (
      <div style={{ padding: 'var(--space-6)' }}>
        <Alert variant="warning">
          No profile found. Complete onboarding first.
        </Alert>
      </div>
    );
  }

  const sectionStyle: React.CSSProperties = {
    padding: 'var(--space-4)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--surface)',
    display: 'grid',
    gap: 'var(--space-3)',
  };
  const statusLine = (st: SectionState) => (
    <span role="status" aria-live="polite" style={{ color: 'var(--success-fg)', fontSize: 'var(--font-size-3xs)', alignSelf: 'center' }}>{st.saved ? 'Saved' : ''}</span>
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: 1040 }}>
      <PageHeader title="Profile" />

      <div style={sectionStyle}>
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--text)' }}>Organization</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <Field id="org-name" label="Name">
            <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} />
          </Field>
          <Field id="org-tier" label="Size">
            <Select value={tier} onChange={(e) => setTier(e.target.value)}>
              <option value="tier_1">Enterprise</option>
              <option value="tier_2">Growth-stage</option>
              <option value="tier_3">Small business</option>
              <option value="tier_4">Solo / project</option>
            </Select>
          </Field>
        </div>
        <CheckboxGroup options={CUSTOMER_TYPE_OPTIONS} value={customerTypes} onChange={setCustomerTypes} columns={3} idPrefix="ps-ct" />
        <CheckboxGroup options={CLASSIFICATION_OPTIONS} value={classifications} onChange={setClassifications} columns={1} idPrefix="ps-cls" />
        {orgState.error ? <Alert variant="error">{orgState.error}</Alert> : null}
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          {statusLine(orgState)}
          <Button variant="secondary" aria-disabled={orgState.saving || undefined} onClick={() => !orgState.saving && saveSection({
            organization_name: orgName, tier, customer_types: customerTypes, service_classifications: classifications,
          }, setOrgState)}>
            {orgState.saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {/* V112-3-10 (ADR-0099): Data & Scope is the workspace defaults every system inherits. */}
      <section style={sectionStyle} aria-labelledby="ps-scope-h">
        <h2 id="ps-scope-h" style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--text)' }}>Data &amp; Scope</h2>
        <WorkspaceScope slug={slug} profile={profile} canEdit={canEdit} canKeep={canKeep} onSaved={setProfile} />
      </section>

      <div style={sectionStyle}>
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--text)' }}>Compliance Goals</h2>
        {frameworks.length > 0 ? (
          <CheckboxGroup
            options={frameworks.map((fw) => ({
              value: fw.framework_id,
              label: fw.short_name || fw.name,
              description: fw.issuing_body ? `${fw.name} · ${fw.issuing_body}` : fw.name,
            }))}
            value={compliancePosture}
            onChange={setCompliancePosture}
            columns={2}
            idPrefix="ps-goals"
          />
        ) : (
          <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: 'var(--font-size-3xs)' }}>
            No frameworks loaded. Check compliance targets in Settings.
          </p>
        )}
        {goalsState.error ? <Alert variant="error">{goalsState.error}</Alert> : null}
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          {statusLine(goalsState)}
          <Button variant="secondary" aria-disabled={goalsState.saving || undefined} onClick={() => !goalsState.saving && saveSection({ compliance_posture: compliancePosture }, setGoalsState)}>
            {goalsState.saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
